import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { get, post } from "../../services/api";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

function QuestionTypesTab() {
  const [questionTypesData, setQuestionTypesData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    control,
  } = useForm({
    defaultValues: {
      id: null,
      name: "",
      code: "",
      description: "",
      hasOptions: true,
      isCustomizable: true,
      isActive: true,
      defaultOptions: [
        { optionText: "", optionValue: "", scoreWeight: 1.0, displayOrder: 1 },
      ],
    },
  });

  // Watch for hasOptions changes
  const hasOptions = watch("hasOptions");
  const defaultOptions = watch("defaultOptions") || [];

  // Fetch question types from API
  const getQuestionTypes = async () => {
    try {
      setLoading(true);
      const response = await get(
        `QAForm/GetQuestionTypes?includeInactive=true`
      );

      if (response && Array.isArray(response)) {
        setQuestionTypesData(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        setQuestionTypesData(response.data);
      } else {
        toast.error("No data received from server");
        setQuestionTypesData([]);
      }
    } catch (error) {
      console.error("Error fetching question types:", error);
      toast.error(error.message || "Failed to load question types");
      setQuestionTypesData([]);
    } finally {
      setLoading(false);
    }
  };

  // Save/Update question type
  const saveQuestionType = async (data) => {
    setIsSubmitting(true);

    try {
      // Transform data to match API DTO
      const apiData = {
        id: data.id || 0,
        name: data.name,
        code: data.code,
        userId: userId,
        description: data.description || "",
        hasOptions: data.hasOptions,
        isCustomizable: data.isCustomizable,
        isActive: data.isActive,
        defaultOptions: data.hasOptions
          ? data.defaultOptions
              .filter((opt) => opt.optionText && opt.optionValue) // Filter out empty options
              .map((option, index) => ({
                optionText: option.optionText,
                optionValue: option.optionValue,
                scoreWeight: parseFloat(option.scoreWeight) || 1.0,
                displayOrder: index + 1,
              }))
          : [],
      };

      const response = data.id
        ? await post("QAForm/UpdateQuestionType", apiData, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
        : await post("QAForm/CreateQuestionType", apiData, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

      if (
        response &&
        (response.isSuccess === true || response.status === "success")
      ) {
        toast.success(response.message || "Question type saved successfully!");
        getQuestionTypes(); // Refresh the list
        resetForm();
      } else {
        toast.error(response?.message || "Failed to save question type");
      }
    } catch (error) {
      console.error("Error saving question type:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to save question type"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit question type
  const handleEdit = async (row) => {
    try {
      const response = await get(`QAForm/GetQuestionTypeById?id=${row.id}`);

      const questionType = response?.data || response;

      if (questionType) {
        reset({
          id: questionType.id,
          name: questionType.name,
          code: questionType.code,
          description: questionType.description || "",
          hasOptions: questionType.hasOptions,
          isCustomizable: questionType.isCustomizable,
          isActive: questionType.isActive,
          defaultOptions:
            questionType.defaultOptions &&
            questionType.defaultOptions.length > 0
              ? questionType.defaultOptions.map((opt) => ({
                  optionText: opt.optionText,
                  optionValue: opt.optionValue,
                  scoreWeight: parseFloat(opt.scoreWeight) || 1.0,
                  displayOrder: opt.displayOrder,
                }))
              : [
                  {
                    optionText: "",
                    optionValue: "",
                    scoreWeight: 1.0,
                    displayOrder: 1,
                  },
                ],
        });
        setIsEditing(true);
      } else {
        toast.error("Failed to load question type data");
      }
    } catch (error) {
      console.error("Error editing question type:", error);
      toast.error(error.message || "Failed to load question type data");
    }
  };

  // Delete question type
  const handleDelete = async (rowId) => {
    const questionType = questionTypesData.find((q) => q.id === rowId);
    if (questionType && !questionType.isCustomizable) {
      toast.error("Default question types cannot be deleted");
      return;
    }

    const confirmDelete = window.confirm(
      "Are you sure you want to delete this question type?"
    );

    if (confirmDelete) {
      try {
        const response = await get(`QAForm/DeleteQuestionType?id=${rowId}`);

        if (
          response &&
          (response.isSuccess === true || response.status === "success")
        ) {
          toast.success(
            response.message || "Question type deleted successfully!"
          );
          getQuestionTypes(); // Refresh the list
        } else {
          toast.error(response?.message || "Failed to delete question type");
        }
      } catch (error) {
        console.error("Error deleting question type:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to delete question type"
        );
      }
    }
  };

  // Toggle status
  const handleToggleStatus = async (rowId) => {
    const questionType = questionTypesData.find((q) => q.id === rowId);
    if (questionType && !questionType.isCustomizable) {
      toast.error("Default question types cannot be deactivated");
      return;
    }
    if (!questionType) return false;

    const confirmToggle = window.confirm(
      `Are you sure you want to ${
        questionType.isActive ? "deactivate" : "activate"
      } this question type?`
    );

    if (confirmToggle) {
      try {
        const response = await get(`QAForm/toggle-status?id=${rowId}`);

        if (
          response &&
          (response.isSuccess === true || response.status === "success")
        ) {
          toast.success(
            response.message ||
              `Question type ${
                questionType.isActive ? "deactivated" : "activated"
              } successfully!`
          );
          getQuestionTypes(); // Refresh the list
        } else {
          toast.error(
            response?.message || "Failed to update question type status"
          );
        }
      } catch (error) {
        console.error("Error toggling status:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update question type status"
        );
      }
    }
  };

  // Add option field - FIXED VERSION
  const addOptionField = () => {
    const currentOptions = getValues("defaultOptions") || [];
    const newDisplayOrder = currentOptions.length + 1;

    const newOptions = [
      ...currentOptions,
      {
        optionText: "",
        optionValue: "",
        scoreWeight: 1.0,
        displayOrder: newDisplayOrder,
      },
    ];

    setValue("defaultOptions", newOptions, { shouldValidate: true });
  };

  // Remove option field - FIXED VERSION
  const removeOptionField = (index) => {
    const currentOptions = getValues("defaultOptions") || [];
    if (currentOptions.length > 1) {
      const newOptions = currentOptions.filter((_, i) => i !== index);
      // Update display order
      const updatedOptions = newOptions.map((opt, idx) => ({
        ...opt,
        displayOrder: idx + 1,
      }));
      setValue("defaultOptions", updatedOptions, { shouldValidate: true });
    }
  };

  // Reset form
  const resetForm = () => {
    reset({
      id: null,
      name: "",
      code: "",
      description: "",
      hasOptions: true,
      isCustomizable: true,
      isActive: true,
      defaultOptions: [
        { optionText: "", optionValue: "", scoreWeight: 1.0, displayOrder: 1 },
      ],
    });
    setIsEditing(false);
  };

  // Input change handler
  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  // Export to Excel
  const exportToExcel = () => {
    const filteredData = questionTypesData.map((row, index) => ({
      "Sr No": index + 1,
      "Type Name": row.name,
      Code: row.code,
      Description: row.description,
      "Has Options": row.hasOptions ? "Yes" : "No",
      Customizable: row.isCustomizable ? "Yes" : "No",
      Status: row.isActive ? "Active" : "Inactive",
      "Question Count": row.questionCount || 0,
      "Created Date":
        row.createdAtFormatted ||
        (row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "N/A"),
      "Last Updated":
        row.updatedAtFormatted ||
        (row.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : "N/A"),
      "Options Count": row.defaultOptions?.length || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Question Types");
    XLSX.writeFile(workbook, "QuestionTypes.xlsx");
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const title = "Question Types";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = 20;
    doc.text(title, titleX, titleY);

    const headers = [
      "Sr No",
      "Type Name",
      "Code",
      "Description",
      "Has Options",
      "Status",
      "Question Count",
      "Options Count",
    ];

    const bodyData = questionTypesData.map((row, index) => [
      index + 1,
      row.name,
      row.code,
      row.description || "-",
      row.hasOptions ? "Yes" : "No",
      row.isActive ? "Active" : "Inactive",
      row.questionCount || 0,
      row.defaultOptions?.length || 0,
    ]);

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 30 },
        2: { cellWidth: 25 },
        3: { cellWidth: 40 },
        4: { cellWidth: 20 },
        5: { cellWidth: 20 },
        6: { cellWidth: 25 },
        7: { cellWidth: 25 },
      },
    });

    doc.save("QuestionTypes.pdf");
  };

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false,
        enableSorting: false,
        size: 60,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "name",
        header: "Type Name",
        size: 150,
      },
      {
        accessorKey: "code",
        header: "Code",
        size: 120,
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 200,
      },
      {
        accessorKey: "hasOptions",
        header: "Has Options",
        size: 100,
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.hasOptions ? "bg-success" : "bg-secondary"
            }`}
          >
            {row.original.hasOptions ? "Yes" : "No"}
          </span>
        ),
      },
      {
        accessorKey: "isCustomizable",
        header: "Customizable",
        size: 100,
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.isCustomizable ? "bg-info" : "bg-warning"
            }`}
          >
            {row.original.isCustomizable ? "Yes" : "No"}
          </span>
        ),
      },
      {
        accessorKey: "questionCount",
        header: "Questions",
        size: 100,
        Cell: ({ row }) => (
          <span className="badge bg-info">
            {row.original.questionCount || 0}
          </span>
        ),
      },
      {
        accessorKey: "isActive",
        header: "Status",
        size: 100,
        Cell: ({ row }) => (
          <span
            className={`badge ${
              row.original.isActive ? "bg-success" : "bg-danger"
            }`}
          >
            {row.original.isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 140,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleEdit(row.original)}
              data-bs-toggle="tooltip"
              title="Edit Question Type"
              disabled={!row.original.isCustomizable}
            >
              <i className="fas fa-pencil"></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleToggleStatus(row.original.id)}
              data-bs-toggle="tooltip"
              title={row.original.isActive ? "Deactivate" : "Activate"}
            >
              <i
                className={`fas ${
                  row.original.isActive ? "fa-ban" : "fa-check"
                }`}
              ></i>
            </button>
            <button
              type="button"
              className="btn p-0"
              onClick={() => handleDelete(row.original.id)}
              data-bs-toggle="tooltip"
              title="Delete Question Type"
              disabled={!row.original.isCustomizable}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // Fetch data on mount
  useEffect(() => {
    getQuestionTypes();
  }, []);

  // Initialize tooltips
  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, [questionTypesData]);

  return (
    <div className="row g-3">
      {/* Left Side - Form */}
      <div className="col-md-6">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">
              {getValues("id") ? "Edit Question Type" : "Add New Question Type"}
            </h5>
          </div>
          <div className="card-body">
            <form
              className="row g-3 new-form-style"
              onSubmit={handleSubmit(saveQuestionType)}
            >
              <input type="hidden" {...register("id")} />

              <div className="col-md-6">
                <label className="form-label form-label-new">
                  Type Name<span className="text-danger fs-9">*</span>
                </label>
                <input
                  {...register("name", {
                    required: "Type name is required.",
                    maxLength: {
                      value: 100,
                      message: "Maximum of 100 characters allowed.",
                    },
                  })}
                  type="text"
                  className="form-control"
                  placeholder="Enter question type name"
                  onChange={handleChangeOfInputWithLengthCheck("name")}
                />
                {errors.name && (
                  <small className="text-danger">{errors.name.message}</small>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label form-label-new">
                  Code<span className="text-danger fs-9">*</span>
                </label>
                <input
                  {...register("code", {
                    required: "Code is required.",
                    maxLength: {
                      value: 50,
                      message: "Maximum of 50 characters allowed.",
                    },
                    pattern: {
                      value: /^[A-Z_]+$/,
                      message:
                        "Only uppercase letters and underscores allowed.",
                    },
                  })}
                  type="text"
                  className="form-control"
                  placeholder="e.g., YES_NO_NA"
                  onChange={handleChangeOfInputWithLengthCheck("code")}
                />
                {errors.code && (
                  <small className="text-danger">{errors.code.message}</small>
                )}
              </div>

              <div className="col-md-12">
                <label className="form-label form-label-new">Description</label>
                <textarea
                  {...register("description", {
                    maxLength: {
                      value: 500,
                      message: "Maximum of 500 characters allowed.",
                    },
                  })}
                  className="form-control"
                  rows="3"
                  placeholder="Enter description"
                  onChange={handleChangeOfInputWithLengthCheck("description")}
                />
                {errors.description && (
                  <small className="text-danger">
                    {errors.description.message}
                  </small>
                )}
              </div>

              <div className="col-md-6">
                <div className="form-check">
                  <input
                    {...register("hasOptions")}
                    className="form-check-input"
                    type="checkbox"
                    id="hasOptions"
                  />
                  <label className="form-check-label" htmlFor="hasOptions">
                    Has Options
                  </label>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-check">
                  <input
                    {...register("isCustomizable")}
                    className="form-check-input"
                    type="checkbox"
                    id="isCustomizable"
                  />
                  <label className="form-check-label" htmlFor="isCustomizable">
                    Customizable
                  </label>
                </div>
              </div>

              {/* Options Section - Only show if hasOptions is true */}
              {hasOptions && (
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header">
                      <h6 className="card-title mb-0">Default Options</h6>
                      <small className="text-muted">
                        Options will be saved in the order shown below
                      </small>
                    </div>
                    <div className="card-body">
                      {defaultOptions.map((option, index) => (
                        <div
                          key={index}
                          className="row g-2 mb-2 border-bottom pb-2"
                        >
                          <div className="col-md-4">
                            <label className="form-label form-label-new fs-10">
                              Option Text<span className="text-danger">*</span>
                            </label>
                            <input
                              {...register(
                                `defaultOptions.${index}.optionText`,
                                {
                                  required: "Text is required",
                                }
                              )}
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g., Yes"
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label form-label-new fs-10">
                              Option Value<span className="text-danger">*</span>
                            </label>
                            <input
                              {...register(
                                `defaultOptions.${index}.optionValue`,
                                {
                                  required: "Value is required",
                                }
                              )}
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g., YES"
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label form-label-new fs-10">
                              Score
                            </label>
                            <input
                              {...register(
                                `defaultOptions.${index}.scoreWeight`,
                                {
                                  valueAsNumber: true,
                                  min: 0,
                                  max: 1,
                                }
                              )}
                              type="number"
                              step="0.1"
                              className="form-control form-control-sm"
                              placeholder="0-1"
                              defaultValue={1.0}
                            />
                          </div>
                          <div className="col-md-2 d-flex align-items-end">
                            <input
                              type="hidden"
                              {...register(
                                `defaultOptions.${index}.displayOrder`
                              )}
                              value={index + 1}
                            />
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => removeOptionField(index)}
                              disabled={defaultOptions.length <= 1}
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-primary btn-sm mt-2"
                        onClick={addOptionField}
                      >
                        <i className="fas fa-plus me-1"></i> Add Option
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="col-md-6">
                <div className="form-check">
                  <input
                    {...register("isActive")}
                    className="form-check-input"
                    type="checkbox"
                    id="isActive"
                    defaultChecked
                  />
                  <label className="form-check-label" htmlFor="isActive">
                    Active
                  </label>
                </div>
              </div>

              <div className="col-12 mt-3">
                <button
                  className="btn btn-primary btn-sm"
                  type="submit"
                  disabled={isSubmitting || loading}
                >
                  {isSubmitting
                    ? "Saving..."
                    : getValues("id")
                    ? "Update Type"
                    : "Add Type"}
                </button>

                {getValues("id") && (
                  <button
                    className="btn btn-secondary btn-sm ms-2"
                    type="button"
                    onClick={resetForm}
                  >
                    Cancel Edit
                  </button>
                )}

                <button
                  className="btn btn-outline-secondary btn-sm ms-2"
                  type="button"
                  onClick={() => {
                    resetForm();
                    toast.info("Form reset");
                  }}
                >
                  Reset Form
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Right Side - Data Table */}
      <div className="col-md-6">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Question Types</h5>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={exportToExcel}
                data-bs-toggle="tooltip"
                title="Export to Excel"
                disabled={loading || questionTypesData.length === 0}
              >
                <i className="fas fa-file-excel me-1"></i> Excel
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={exportToPDF}
                data-bs-toggle="tooltip"
                title="Export to PDF"
                disabled={loading || questionTypesData.length === 0}
              >
                <i className="fas fa-file-pdf me-1"></i> PDF
              </button>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading question types...</p>
              </div>
            ) : questionTypesData.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                <p className="text-muted">No question types found</p>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={getQuestionTypes}
                >
                  <i className="fas fa-sync me-1"></i> Refresh
                </button>
              </div>
            ) : (
              <div className="table-container fixed-last-column-table">
                <CustomDatatableGrid
                  columns={columns}
                  data={questionTypesData}
                  onExportExcel={exportToExcel}
                  onExportPDF={exportToPDF}
                  initialState={{
                    columnPinning: { right: ["actions"] },
                    columnVisibility: {
                      description: false,
                    },
                    pagination: {
                      pageSize: 10,
                      pageIndex: 0,
                    },
                  }}
                  enableRowSelection={false}
                  enableColumnFilters={true}
                  enableGlobalFilter={true}
                  enablePagination={true}
                  enableSorting={true}
                  enableColumnResizing={true}
                  muiTablePaperProps={{
                    elevation: 0,
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "500px",
                    },
                  }}
                  renderTopToolbarCustomActions={() => (
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold">
                        Total: {questionTypesData.length}
                      </span>
                      <span className="badge bg-success">
                        Active:{" "}
                        {questionTypesData.filter((q) => q.isActive).length}
                      </span>
                      <span className="badge bg-info">
                        Customizable:{" "}
                        {
                          questionTypesData.filter((q) => q.isCustomizable)
                            .length
                        }
                      </span>
                    </div>
                  )}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuestionTypesTab;
